import React, { useMemo, useState } from "react";
import { Card, Row, Col, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { ColumnDef } from "@tanstack/react-table";
import CurrencyFormat from "react-currency-format";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronRight,
  faChevronDown,
} from "@fortawesome/pro-solid-svg-icons";
import DataTable from "@components/DataTable";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import ScopeStackModal from "@components/ScopeStackModal/ScopeStackModal";
import FormNumberField from "@components/Forms/FormNumberField";
import { handleAlert } from "@utils/helperFunctions";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { PricingRow } from "../types/PricingRow";
import useStyles from "../styles/useStyles";

interface Props {
  rows: PricingRow[];
  currencyUnit: string;
  isLoading: boolean;
  totalTcv: number;
  totalCost: number;
  totalProfit: number;
  totalMargin: number;
  paymentCreditsTotal: number | null;
  customerPaymentsTotal: number | null;
  showMarginModal: boolean;
  onOpenMarginModal: () => void;
  projectMargin: string;
  setProjectMargin: (value: string) => void;
  marginError: string[] | null;
  onApplyMargin: () => void;
  onCancelMargin: () => void;
}

const Show: React.FC<Props> = ({
  rows,
  currencyUnit,
  isLoading,
  totalTcv,
  totalCost,
  totalProfit,
  totalMargin,
  paymentCreditsTotal,
  customerPaymentsTotal,
  showMarginModal,
  onOpenMarginModal,
  projectMargin,
  setProjectMargin,
  marginError,
  onApplyMargin,
  onCancelMargin,
}) => {
  const styles = useStyles();
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const displayRows: PricingRow[] = useMemo(() => {
    const result: PricingRow[] = [];
    for (const row of rows) {
      result.push(row);
      if (
        row.rowType === "data" &&
        expandedRows.has(row.id) &&
        row.detailRows?.length
      ) {
        for (const detail of row.detailRows) {
          result.push({
            id: `detail-${detail.id}`,
            source: detail.source,
            revenue: detail.revenue,
            adjustment: 0,
            net: 0,
            tcv: detail.tcv,
            cost: detail.cost,
            profit: detail.profit,
            margin: detail.margin,
            rowType: "detail",
          });
        }
      }
    }
    result.push({
      id: "total",
      source: "TOTAL",
      revenue: 0,
      adjustment: 0,
      net: 0,
      tcv: totalTcv,
      cost: totalCost,
      profit: totalProfit,
      margin: totalMargin,
      rowType: "total",
    });
    return result;
  }, [rows, expandedRows, totalTcv, totalCost, totalProfit, totalMargin]);

  const formatCurrency = (value: number) => (
    <CurrencyFormat
      displayType="text"
      prefix={currencyUnit}
      isNumericString={true}
      thousandSeparator={true}
      value={value.toFixed(2)}
    />
  );

  const columns: ColumnDef<PricingRow>[] = [
    {
      header: "Source",
      cell(props) {
        const row = props.row.original;
        const isSection = row.rowType === "section";
        const isDetail = row.rowType === "detail";
        const isData = row.rowType === "data";
        const isTotal = row.rowType === "total";
        const hasDetails = isData && (row.detailRows?.length ?? 0) > 0;
        const isExpanded = expandedRows.has(row.id);

        if (isTotal) return null;

        return (
          <span
            style={{
              fontWeight: isSection ? 700 : 400,
              paddingLeft: isDetail ? 40 : isSection ? 0 : 20,
            }}
          >
            {row.source}
            {hasDetails && (
              <OverlayTrigger
                placement="top"
                delay={{ show: 250, hide: 400 }}
                overlay={
                  <Tooltip id={`tooltip-${row.id}`}>
                    {isExpanded ? "Hide Details" : "Show Details"}
                  </Tooltip>
                }
              >
                <span
                  className={`expand-icon ${styles.expandIcon}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleRow(row.id);
                  }}
                >
                  <FontAwesomeIcon
                    icon={isExpanded ? faChevronDown : faChevronRight}
                    size="xs"
                  />
                </span>
              </OverlayTrigger>
            )}
          </span>
        );
      },
    },
    {
      header: "Revenue",
      cell(props) {
        const row = props.row.original;
        if (row.rowType === "section") return null;
        if (row.rowType === "total") return null;
        return <span>{formatCurrency(row.revenue)}</span>;
      },
    },
    {
      header: "Adjustment",
      cell(props) {
        const row = props.row.original;
        if (row.rowType === "section") return null;
        if (row.rowType === "total") return null;
        if (row.rowType === "detail") return <span>---</span>;
        return <span>{formatCurrency(row.adjustment)}</span>;
      },
    },
    {
      header: "Net",
      cell(props) {
        const row = props.row.original;
        if (row.rowType === "section") return null;
        if (row.rowType === "total")
          return <span style={{ fontWeight: 700 }}>TOTAL</span>;
        if (row.rowType === "detail")
          return <span>{formatCurrency(row.tcv)}</span>;
        return <span>{formatCurrency(row.net)}</span>;
      },
    },
    {
      header: "TCV",
      cell(props) {
        const row = props.row.original;
        if (row.rowType === "section") return null;
        if (row.rowType === "total")
          return (
            <span style={{ fontWeight: 700 }}>
              {formatCurrency(row.tcv)}
            </span>
          );
        return <span>{formatCurrency(row.tcv)}</span>;
      },
    },
    {
      header: "Cost",
      cell(props) {
        const row = props.row.original;
        if (row.rowType === "section") return null;
        if (row.rowType === "total")
          return (
            <span style={{ fontWeight: 700, color: "#b90200" }}>
              {formatCurrency(row.cost)}
            </span>
          );
        return <span>{formatCurrency(row.cost)}</span>;
      },
    },
    {
      header: "Profit",
      cell(props) {
        const row = props.row.original;
        if (row.rowType === "section") return null;
        if (row.rowType === "total")
          return (
            <span style={{ fontWeight: 700, color: "#418172" }}>
              {formatCurrency(row.profit)}
            </span>
          );
        return <span>{formatCurrency(row.profit)}</span>;
      },
    },
    {
      header: "Margin",
      cell(props) {
        const row = props.row.original;
        if (row.rowType === "section") return null;
        if (row.rowType === "total") {
          return (
            <span style={{ fontWeight: 700, color: "#418172" }}>
              {row.tcv === 0 ? "---" : `${row.margin!.toFixed(2)}%`}
            </span>
          );
        }
        const margin = row.margin;
        if (margin === null || isNaN(margin)) return <span>---</span>;
        return <span>{margin.toFixed(2)}%</span>;
      },
    },
  ];

  const textRight = { textAlign: "right" as const };

  return (
    <div className={`${styles.page} ${styles.rightAlignColumns}`}>
      {handleAlert(
        !!marginError,
        marginError || [],
        () => {},
        "warning",
        ToastAlert
      )}
      <Card className={styles.card}>
        <Card.Header className={styles.cardHeader}>
          <Row className={styles.headerRow}>
            <Col className={styles.title}>Overall Pricing</Col>
            <Col className="text-end" style={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
              <Button
                className="btnSeawhite"
                onClick={onOpenMarginModal}
              >
                Adjust Project Margin
              </Button>
            </Col>
          </Row>
        </Card.Header>
        <hr className={styles.hr} />
        <Card.Body>
          {isLoading ? (
            <ScopeStackSpinner />
          ) : (
            <>
              <DataTable
                data={displayRows}
                columns={columns}
                selectable={false}
                striped
                hover
              />
              {paymentCreditsTotal !== null && (
                <>
                  <Row className={styles.totalRow}>
                    <Col></Col>
                    <Col style={textRight} xs={4}>
                      Payment Credits
                    </Col>
                    <Col style={textRight}>
                      {formatCurrency(paymentCreditsTotal)}
                    </Col>
                    <Col></Col>
                    <Col></Col>
                    <Col></Col>
                  </Row>
                  <Row className={styles.totalRow}>
                    <Col></Col>
                    <Col style={textRight} xs={4}>
                      Customer Payments
                    </Col>
                    <Col style={textRight}>
                      {formatCurrency(customerPaymentsTotal!)}
                    </Col>
                    <Col></Col>
                    <Col></Col>
                    <Col></Col>
                  </Row>
                </>
              )}
            </>
          )}
        </Card.Body>
      </Card>
      <ScopeStackModal
        modalTitle="Edit Project Margin"
        modalBody={
          <div>
            <FormNumberField
              label="New Project Margin (%)"
              placeholder="Enter the percent you want the new project margin to be"
              step="0.01"
              value={projectMargin}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setProjectMargin(e.target.value)
              }
              min={undefined}
              max={undefined}
              required={false}
              defaultValue={undefined}
              readOnly={false}
              fieldFooter={undefined}
            />
          </div>
        }
        button1Text="Cancel"
        handleButton1Click={onCancelMargin}
        button2Text="Apply Margin"
        handleButton2Click={onApplyMargin}
        show={showMarginModal}
        handleClose={onCancelMargin}
      />
    </div>
  );
};

export default Show;
